import {
  BookingStatusChip,
  Card,
  DataTable,
  ErrorState,
  PageHeader,
  StatTile,
  type ColumnDef,
} from '@zuruflex/ui';
import { useThreadMetrics, useThreads } from '@zuruflex/api-client/react';
import { formatDateIn, formatMoneyIn, toLocale } from '@zuruflex/i18n';
import type { Booking } from '@zuruflex/types';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronRight,
  Clock,
  Handshake,
  LifeBuoy,
  TimerOff,
  TrendingUp,
  UserRoundX,
  Wallet,
  Wrench,
} from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useTableLabels } from '../lib/table-labels.js';

/** Same calendar day in the viewer's zone — which is the zone a shift runs in. */
function isToday(value: string): boolean {
  const date = new Date(value);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

function timeIn(value: string, locale: string): string {
  return new Date(value).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/**
 * The operations console.
 *
 * A partner's overview answers "how is my car doing"; this answers "what
 * has to happen today and what is stuck" — so it is built from the live end
 * of the book rather than from totals. Three bands: the shift's numbers,
 * then the two queues someone actually works from, then the book itself.
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();

  const fleet = useQuery({
    queryKey: ['dashboard', 'fleet'],
    queryFn: () => api.listVehicles({ perPage: 100 }),
  });

  /*
   * Today's movements can only come from bookings that are confirmed or
   * already running, so those two statuses are fetched rather than the
   * whole book — the completed history behind them is far larger than a
   * page and none of it is due to move.
   */
  const live = useQuery({
    queryKey: ['dashboard', 'live'],
    queryFn: async () => {
      const [confirmed, active] = await Promise.all([
        api.listBookings({ status: 'CONFIRMED', perPage: 100 }),
        api.listBookings({ status: 'ACTIVE', perPage: 100 }),
      ]);
      return [...confirmed.items, ...active.items];
    },
  });

  /* Counts only — `total` is the answer, so a single row is enough to ask. */
  const awaitingPayment = useQuery({
    queryKey: ['dashboard', 'awaitingPayment'],
    queryFn: () => api.listBookings({ status: 'AWAITING_PAYMENT', perPage: 1 }),
  });

  const pendingPartners = useQuery({
    queryKey: ['dashboard', 'pendingPartners'],
    queryFn: () => api.listPartners({ status: 'PENDING', perPage: 1 }),
  });

  const bookings = useQuery({
    queryKey: ['dashboard', 'bookings'],
    queryFn: () => api.listBookings({ perPage: 10 }),
  });

  /*
   * The support desk, as three numbers and three ways in.
   *
   * `perPage: 1` because `total` is the answer — a single row is enough to
   * ask how many there are, and the queue itself is one click away rather
   * than duplicated here.
   */
  const openSupport = useThreads(api, {
    kind: 'SUPPORT',
    status: ['OPEN', 'WAITING'],
    perPage: 1,
  });
  const unassignedSupport = useThreads(api, {
    kind: 'SUPPORT',
    assignee: 'none',
    status: ['OPEN', 'WAITING'],
    perPage: 1,
  });
  /*
   * How many people are still waiting for a first reply, which is the one
   * figure a list cannot produce — and the metrics response is also where
   * the SLA targets live, since `GET /v1/settings` is MANAGER-only. Fixed
   * at mount so the query key does not change on every render.
   */
  const today = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: new Date().toISOString() };
  }, []);
  const support = useThreadMetrics(api, today);

  // Columns depend on the language, so they are rebuilt when it changes.
  const columns = useMemo<ColumnDef<Booking>[]>(
    () => [
      {
        accessorKey: 'reference',
        header: t('common:field.reference'),
        cell: ({ row }) => <span className="font-mono font-semibold">{row.original.reference}</span>,
      },
      {
        accessorKey: 'status',
        header: t('common:field.status'),
        cell: ({ row }) => (
          <BookingStatusChip
            status={row.original.status}
            label={t(`common:bookingStatus.${row.original.status}`)}
          />
        ),
      },
      {
        accessorKey: 'pickupAt',
        header: t('common:field.pickUp'),
        cell: ({ row }) => formatDateIn(row.original.pickupAt, locale),
      },
      { accessorKey: 'days', header: t('common:field.days') },
      {
        accessorKey: 'total',
        header: t('common:field.total'),
        cell: ({ row }) => (
          <span className="font-semibold tabular-nums">
            {formatMoneyIn(row.original.total, row.original.currency, locale)}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  const vehicles = fleet.data?.items ?? [];
  const available = vehicles.filter((vehicle) => vehicle.status === 'AVAILABLE').length;
  const onHire = vehicles.filter((vehicle) => vehicle.status === 'RENTED').length;
  const inWorkshop = vehicles.filter((vehicle) => vehicle.status === 'MAINTENANCE').length;

  // Utilisation is the number the owner actually checks each morning.
  const bookable = available + onHire;
  const utilisation = bookable > 0 ? Math.round((onHire / bookable) * 100) : 0;

  const liveBookings = live.data ?? [];
  const goingOut = liveBookings
    .filter((booking) => isToday(booking.pickupAt))
    .sort((a, b) => a.pickupAt.localeCompare(b.pickupAt));
  const comingBack = liveBookings
    .filter((booking) => isToday(booking.dropoffAt))
    .sort((a, b) => a.dropoffAt.localeCompare(b.dropoffAt));

  const queue: { key: string; icon: ReactNode; label: string; count: number; to: string }[] = [
    {
      key: 'partners',
      icon: <Handshake className="size-4" />,
      label: t('dashboard.queuePartners'),
      count: pendingPartners.data?.total ?? 0,
      to: '/partners',
    },
    {
      key: 'payment',
      icon: <Wallet className="size-4" />,
      label: t('dashboard.queuePayment'),
      count: awaitingPayment.data?.total ?? 0,
      to: '/bookings',
    },
    {
      key: 'workshop',
      icon: <Wrench className="size-4" />,
      label: t('dashboard.queueWorkshop'),
      count: inWorkshop,
      to: '/fleet',
    },
  ];
  // A queue with nothing in it is not a task, so it does not earn a row.
  const openQueue = queue.filter((item) => item.count > 0);

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.subtitle')}
        action={
          <span className="inline-flex items-center gap-2 rounded-pill border border-border-default px-3 py-1.5 text-xs font-medium text-text-muted">
            <Clock className="size-3.5" />
            {formatDateIn(new Date(), locale)}
          </span>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label={t('dashboard.utilisation')}
          value={`${utilisation}%`}
          hint={t('dashboard.utilisationHint', { out: onHire, total: bookable })}
          icon={<TrendingUp className="size-4" />}
          accent
          isLoading={fleet.isPending}
        />
        <StatTile
          label={t('dashboard.outToday')}
          value={goingOut.length}
          hint={t('dashboard.outTodayHint')}
          icon={<ArrowUpRight className="size-4" />}
          isLoading={live.isPending}
        />
        <StatTile
          label={t('dashboard.dueBack')}
          value={comingBack.length}
          hint={t('dashboard.dueBackHint')}
          icon={<ArrowDownLeft className="size-4" />}
          isLoading={live.isPending}
        />
        <StatTile
          label={t('dashboard.inWorkshop')}
          value={inWorkshop}
          hint={inWorkshop > 0 ? t('dashboard.workshopBusy') : t('dashboard.workshopClear')}
          icon={<Wrench className="size-4" />}
          isLoading={fleet.isPending}
        />
      </div>

      {/*
        The desk, beside the fleet. Each tile is the link an agent would
        have built by hand — the filters live in the query string, so a
        number on a console is a way into the queue it counts.
      */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatTile
          label={t('inbox.title')}
          value={openSupport.data?.total ?? 0}
          hint={t('inbox.subtitle')}
          icon={<LifeBuoy className="size-4" />}
          isLoading={openSupport.isPending}
          to="/inbox"
        />
        <StatTile
          label={t('inbox.filter.unassigned')}
          value={unassignedSupport.data?.total ?? 0}
          icon={<UserRoundX className="size-4" />}
          isLoading={unassignedSupport.isPending}
          to="/inbox?view=unassigned"
        />
        <StatTile
          label={t('reports.unanswered')}
          value={support.data?.firstResponseMinutes.unanswered ?? 0}
          icon={<TimerOff className="size-4" />}
          isLoading={support.isPending}
          to="/inbox?view=breaching"
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="font-display text-lg font-bold">{t('dashboard.needsAction')}</h2>
          {openQueue.length === 0 ? (
            <p className="mt-3 text-sm text-text-muted">{t('dashboard.needsActionClear')}</p>
          ) : (
            <ul className="mt-3 divide-y divide-border-default">
              {openQueue.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => navigate(item.to)}
                    className="group flex w-full items-center gap-3 rounded-field px-2 py-3 text-left transition-colors hover:bg-surface-hover"
                  >
                    <span className="text-text-muted">{item.icon}</span>
                    <span className="flex-1 text-sm font-medium">{item.label}</span>
                    <span className="rounded-pill bg-accent-soft px-2.5 py-0.5 text-xs font-bold tabular-nums text-accent-soft-text">
                      {item.count}
                    </span>
                    <ChevronRight className="size-4 shrink-0 text-text-subtle group-hover:text-text" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="font-display text-lg font-bold">{t('dashboard.movements')}</h2>
          {live.isPending ? (
            <p className="mt-3 text-sm text-text-muted">{t('dashboard.movementsLoading')}</p>
          ) : goingOut.length === 0 && comingBack.length === 0 ? (
            <p className="mt-3 text-sm text-text-muted">{t('dashboard.movementsClear')}</p>
          ) : (
            <div className="mt-3 grid gap-5 sm:grid-cols-2">
              <MovementList
                title={t('dashboard.out')}
                icon={<ArrowUpRight className="size-3.5" />}
                bookings={goingOut}
                at={(booking) => booking.pickupAt}
                locale={i18n.language}
                emptyLabel={t('dashboard.outClear')}
                onSelect={(reference) => navigate(`/bookings/${reference}`)}
              />
              <MovementList
                title={t('dashboard.back')}
                icon={<ArrowDownLeft className="size-3.5" />}
                bookings={comingBack}
                at={(booking) => booking.dropoffAt}
                locale={i18n.language}
                emptyLabel={t('dashboard.backClear')}
                onSelect={(reference) => navigate(`/bookings/${reference}`)}
              />
            </div>
          )}
        </Card>
      </div>

      <h2 className="mb-4 mt-10 font-display text-xl font-bold">{t('dashboard.latest')}</h2>

      {bookings.error ? (
        <ErrorState error={bookings.error} onRetry={() => void bookings.refetch()} />
      ) : (
        <DataTable
          labels={tableLabels}
          columns={columns}
          data={bookings.data?.items ?? []}
          isLoading={bookings.isPending}
          paginate={false}
          onRowClick={(row) => navigate(`/bookings/${row.reference}`)}
          emptyMessage={t('dashboard.noBookings')}
        />
      )}
    </>
  );
}

interface MovementListProps {
  title: string;
  icon: ReactNode;
  bookings: Booking[];
  /** Which end of the hire this column is timed against. */
  at: (booking: Booking) => string;
  locale: string;
  emptyLabel: string;
  onSelect: (reference: string) => void;
}

/** One side of today: the cars going out, or the cars coming back. */
function MovementList({
  title,
  icon,
  bookings,
  at,
  locale,
  emptyLabel,
  onSelect,
}: MovementListProps) {
  return (
    <div>
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-text-muted">
        {icon}
        {title}
      </p>
      {bookings.length === 0 ? (
        <p className="mt-2 text-sm text-text-subtle">{emptyLabel}</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {bookings.map((booking) => (
            <li key={booking.id}>
              <button
                type="button"
                onClick={() => onSelect(booking.reference)}
                className="flex w-full items-center justify-between gap-3 rounded-field px-2 py-1.5 text-left transition-colors hover:bg-surface-hover"
              >
                <span className="truncate font-mono text-xs font-semibold">
                  {booking.reference}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-text-muted">
                  {timeIn(at(booking), locale)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
